import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card } from "../../components/ui/card";
import { Loader2, Save, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const empty = {
  sub_broker_code: "", sub_broker_name: "", email: "", mobile: "", password: "",
  arn_code: "", euin: "", pan: "", gst: "",
  address: "", city: "", state: "", pincode: "",
  rm_code: "", branch_code: "", status: "active",
  revenue_visibility: false, report_download_access: true, transaction_access: false,
};

export default function SubBrokerForm() {
  const { id } = useParams();
  const editing = Boolean(id);
  const { api } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(editing);

  useEffect(() => {
    if (!editing) return;
    (async () => {
      try {
        const { data } = await api.get(`/subbroker/${id}`);
        setForm({ ...empty, ...data, password: "" });
      } catch (e) {
        toast.error("Failed to load sub broker"); navigate("/sub-brokers/list");
      } finally { setLoading(false); }
    })();
    /* eslint-disable-next-line */
  }, [id]);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.put(`/subbroker/${id}`, payload);
        toast.success("Sub broker updated");
      } else {
        await api.post("/subbroker/create", form);
        toast.success("Sub broker created · login credentials issued");
      }
      navigate("/sub-brokers/list");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-500" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="subbroker-form-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/sub-brokers/list")}><ArrowLeft className="w-4 h-4" /></Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-800">{editing ? "Edit Sub Broker" : "Create Sub Broker"}</h1>
          <p className="text-sm text-gray-500">{editing ? "Update profile, access flags and password" : "All mandatory fields are marked with *"}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card className="p-5 space-y-4">
          <h3 className="font-semibold text-lg text-gray-800">Basic Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Associate Code *" value={form.sub_broker_code} onChange={(v) => setField("sub_broker_code", v)} disabled={editing} required testid="sub_broker_code" />
            <Field label="Sub Broker Name *" value={form.sub_broker_name} onChange={(v) => setField("sub_broker_name", v)} required testid="sub_broker_name" />
            <Field label="Email *" type="email" value={form.email} onChange={(v) => setField("email", v)} required disabled={editing} testid="email" />
            <Field label="Mobile *" value={form.mobile} onChange={(v) => setField("mobile", v)} required testid="mobile" />
            <Field label={editing ? "New Password (leave blank to keep)" : "Password *"} type="password" value={form.password} onChange={(v) => setField("password", v)} required={!editing} testid="password" />
            <Field label="ARN Code" value={form.arn_code} onChange={(v) => setField("arn_code", v)} testid="arn_code" />
            <Field label="EUIN" value={form.euin} onChange={(v) => setField("euin", v)} testid="euin" />
            <div className="space-y-1">
              <Label className="text-xs">Status *</Label>
              <select value={form.status} onChange={(e) => setField("status", e.target.value)} className="w-full border rounded-md h-10 px-3 text-sm" data-testid="status">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </Card>

        <Card className="p-5 space-y-4">
          <h3 className="font-semibold text-lg text-gray-800">Business Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="PAN" value={form.pan} onChange={(v) => setField("pan", v.toUpperCase())} testid="pan" />
            <Field label="GST" value={form.gst} onChange={(v) => setField("gst", v)} testid="gst" />
            <Field label="Address" value={form.address} onChange={(v) => setField("address", v)} testid="address" />
            <Field label="City" value={form.city} onChange={(v) => setField("city", v)} testid="city" />
            <Field label="State" value={form.state} onChange={(v) => setField("state", v)} testid="state" />
            <Field label="Pincode" value={form.pincode} onChange={(v) => setField("pincode", v)} testid="pincode" />
          </div>
        </Card>

        <Card className="p-5 space-y-4">
          <h3 className="font-semibold text-lg text-gray-800">Mapping & Access</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="RM Code (free text)" value={form.rm_code} onChange={(v) => setField("rm_code", v)} testid="rm_code" />
            <Field label="Branch Code (free text)" value={form.branch_code} onChange={(v) => setField("branch_code", v)} testid="branch_code" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Checkbox label="Revenue Visibility" checked={form.revenue_visibility} onChange={(v) => setField("revenue_visibility", v)} testid="revenue_visibility" />
            <Checkbox label="Report Download Access" checked={form.report_download_access} onChange={(v) => setField("report_download_access", v)} testid="report_download_access" />
            <Checkbox label="Transaction Access" checked={form.transaction_access} onChange={(v) => setField("transaction_access", v)} testid="transaction_access" />
          </div>
        </Card>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate("/sub-brokers/list")}>Cancel</Button>
          <Button type="submit" disabled={saving} style={{ backgroundColor: "#1a3f3a" }} data-testid="subbroker-form-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            {editing ? "Save Changes" : "Create Sub Broker"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, disabled, testid }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} required={required} disabled={disabled} data-testid={`sbf-${testid}`} />
    </div>
  );
}

function Checkbox({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={`sbf-${testid}`} />
      <span>{label}</span>
    </label>
  );
}
